import { HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BankDataDto } from '@app/data/dtos/ptrab/payslips/bank-data.dto';
import { CarTransferDto } from '@app/data/dtos/ptrab/payslips/car-transer.dto';
import { DirectDebitDto } from '@app/data/dtos/ptrab/payslips/direct-debit.dto';
import { GarnishAmountPayslipDto, GarnishmentDto } from '@app/data/dtos/ptrab/payslips/garnishment.dto';
import { PayslipDto } from '@app/data/dtos/ptrab/payslips/payslip.dto';
import {
  mapBankData,
  mapCarTransferData,
  mapDirectDebitData,
  mapGarnishmentData,
  mapPayslipData,
  mapUpdateDirectDebit
} from '@app/data/mappers/ptrab/payslips/payslips.mappers';
import { PayslipstRepository } from '@app/domain/repositories/ptrab/payslips/payslips.repository.contract';
import { IDataObjectResponse, IDataResponse } from '@app/entities/interfaces/data/data.interface';
import { TwoFactorAuthorization } from '@app/entities/interfaces/ptrab/two-factor-authorization.interface';
import { ErrorCodes } from '@app/entities/models/error/error.model';
import { BankData } from '@app/entities/models/ptrab/payslips/bank-data.model';
import { CarTransfer } from '@app/entities/models/ptrab/payslips/car-transfer.model';
import { DirectDebit } from '@app/entities/models/ptrab/payslips/direct-debit.model';
import { GarnishAmountPayslip, Garnishment } from '@app/entities/models/ptrab/payslips/garnishment.model';
import { Payslip } from '@app/entities/models/ptrab/payslips/payslip.model';
import { Logger, AlertService } from '@app/services';
import { ENV } from '@environments/environment';
import { SECTION_KEY } from '@services/interceptors/error-sections.interceptor/error-sections.interceptor';

import { MTranslateService } from '@mercadona/core/translate';
import { MRepository } from '@mercadona/core/utils/repository';

@Injectable()
export class PayslipsRepositoryImpl extends MRepository implements PayslipstRepository {
  currentStatus!: ErrorCodes | null;
  payslipUrl = '/ptrab/payslip';
  translateService: MTranslateService = inject(MTranslateService);
  private logger = new Logger('PayslipsService');

  constructor(private alertService: AlertService) {
    super(`${ENV.API_URL_PTRAB}`);
  }

  getPayslips(isEmployee: boolean): Observable<Payslip[]> {
    const url = isEmployee ? this.payslipUrl : `${this.payslipUrl}/exemployee`;

    return this.get<IDataResponse<PayslipDto[]>>(url, { context: new HttpContext().set(SECTION_KEY, 'payslips') }).pipe(
      map(mapPayslipData)
    );
  }

  getGarnishmentList(): Observable<Garnishment[]> {
    return this.get<IDataResponse<GarnishmentDto[]>>(`${this.payslipUrl}/garnishment`, {
      context: new HttpContext().set(SECTION_KEY, 'garnishments')
    }).pipe(map(mapGarnishmentData));
  }

  getCarTransferList(): Observable<CarTransfer[]> {
    return this.get<IDataResponse<CarTransferDto[]>>(`${this.payslipUrl}/car-transfer`, {
      context: new HttpContext().set(SECTION_KEY, 'car-transfer')
    }).pipe(map(mapCarTransferData));
  }

  getDirectDebits(): Observable<DirectDebit[]> {
    return this.get<IDataObjectResponse<DirectDebitDto[], 'companies'>>(`${this.payslipUrl}/direct-debit`, {
      context: new HttpContext().set(SECTION_KEY, 'direct-debit')
    }).pipe(map(mapDirectDebitData));
  }

  async arePayslipsAvailable(showToast = true): Promise<boolean | null> {
    this.currentStatus = null;

    try {
      await firstValueFrom(this.get(`${this.payslipUrl}/status`));
    } catch (error: any) {
      this.currentStatus = error?.status;

      switch (error?.status) {
        case ErrorCodes.LOCKED:
          if (showToast) {
            this.alertService.showWarning(
              this.translateService.translate('IRPF.REGULARIZATION.MODIFICATION_UNAVAILABLE.TITLE'),
              this.translateService.translate('TWO_FACTOR.SERVICE_LOCKED.MESSAGE')
            );
          }
          return false;
        case ErrorCodes.UNAUTHORIZED:
          return false;
        default:
          return null;
      }
    }
    return true;
  }

  calculateGarnishPayslip(company_code: string): Observable<GarnishAmountPayslip> {
    return this.get<GarnishAmountPayslipDto>(`${this.payslipUrl}/garnishment/calculate`, { params: { company_code } });
  }

  async isDirectDebitCertificateAvaible(): Promise<boolean> {
    try {
      await firstValueFrom(this.get(`${this.payslipUrl}/direct-debit/status-certificate`));
    } catch (error: any) {
      this.logger.error(error);

      if (error && error.status === ErrorCodes.NOT_FOUND) {
        this.alertService.showError(
          this.translateService.translate('COMMON.ERROR'),
          this.translateService.translate('CERTIFICATE.CHANGE_DIRECT_DEBIT')
        );
      }
      return Promise.resolve(false);
    }

    return Promise.resolve(true);
  }

  modifyDirectDebit(directDebit: DirectDebit, authorization: TwoFactorAuthorization): Observable<DirectDebit> {
    const request = {
      body: {
        params: mapUpdateDirectDebit(directDebit),
        authorization
      }
    };
    return this.post<DirectDebitDto>(`${this.payslipUrl}/direct-debit`, request);
  }

  getBankDataName(iban: string): Observable<BankData> {
    const body = {
      iban
    };

    return this.post<BankDataDto>(`${this.payslipUrl}/direct-debit/bank-data`, {
      body
    }).pipe(map(mapBankData));
  }
}
